import { useEffect } from 'react';
import { css } from '@emotion/react';
import { Link, useRouter as useRouterLegacy } from '@tanstack/react-location';
import { useRemoveAppLoader } from '@frontend/document';
import { useTranslation } from '@frontend/i18n';
import { DelayedRender } from '@frontend/timer';
import { sentry } from '@frontend/tracking';
import { useRouter } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Heading, TextLink } from '@frontend/design-system';
import { useApp } from '../providers/app.provider';
import { useRoutes } from '../providers/routes.provider';

export default function NotFoundPage() {
  const router = useRouter();
  const legacyRouter = useRouterLegacy();

  useRemoveAppLoader({ removeOnMount: true });

  //The current router sync updates the router state, but it takes an extra render to update the location
  //So the 404 page can flash for a second. This check just makes sure to render nothing to prevent this flash.
  if (router.state.location.pathname !== legacyRouter.state.location.pathname) {
    return null;
  }

  return (
    <DelayedRender delay={300} fallback={null}>
      <Main404 />
    </DelayedRender>
  );
}

const Main404 = () => {
  const { refresh, hasAlreadyRefreshed } = useApp();
  const { isRedirecting } = useRoutes();
  const { t } = useTranslation();
  const router = useRouter();
  const legacyRouter = useRouterLegacy();
  const latestLocation = router.latestLocation;

  useEffect(() => {
    if (hasAlreadyRefreshed('not-found-page-refresh')) {
      return;
    }

    const timeout = setTimeout(() => {
      sentry.error({
        error: 'Legacy Router: Not Found Page',
        topic: 'infra',
        team: 'users-infra',
        severityLevel: 'error',
        addContext: {
          name: 'Router State',
          context: {
            latestLocationPathname: latestLocation.pathname,
            matches: router.state?.matches.length,
            windowPathname: window.location.pathname,
            routerMatches: router.state.matches?.length,
            legacyRouterLocation: legacyRouter.state?.location?.pathname,
            legacyRouterMatches: legacyRouter.state?.matches?.length,
          },
        },
      });
      // refresh('hard', 'not-found-page-refresh');
    }, 300);
    return () => {
      clearTimeout(timeout);
    };
  }, [latestLocation, router.state, legacyRouter.state, refresh, hasAlreadyRefreshed]);

  return (
    <main
      css={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {isRedirecting ? (
        <Heading
          css={css`
            color: ${theme.colors.neutral30};
            margin-top: ${theme.spacing(2)};
          `}
          level={3}
        >
          {t('Redirecting to New Page...')}
        </Heading>
      ) : (
        <>
          <Heading css={{ 'margin-bottom': theme.spacing(1) }}>{t('404 - Page Not Found')}</Heading>
          <TextLink as={Link} to={'/'}>
            {t('Go home')}
          </TextLink>
        </>
      )}
    </main>
  );
};
